// Versioned, portable plan compiler. No titles, wall clocks, network or score writes.
export const STRUCTURED_TRAINING_TEMPLATES = ["tennis_interval", "sidestep"];
const invalid = () => ({ ok: false, reason: "incompletePlan", plan: null });
const record = value => value && typeof value === "object" && !Array.isArray(value);
const keys = (value, allowed) => record(value) && Object.keys(value).every(key => allowed.includes(key));
const numeric = (value, min, max) => typeof value === "number" && Number.isFinite(value) && value >= min && value <= max;
const count = value => numeric(value, 1, 100) && Number.isInteger(value);
const id = value => typeof value === "string" && /^[a-zA-Z0-9_-]{1,64}$/.test(value);
const optionalTime = value => value == null || numeric(value, 1, 7200);
const ms = seconds => seconds * 1000;
const optionalId = value => value == null || id(value);
const exerciseSnapshot = value => value == null || (keys(value, ["id", "version", "title", "title_en", "description", "description_en", "icon_id", "video_id"])
  && id(value.id) && Number.isInteger(value.version) && value.version > 0
  && ["title", "title_en", "description", "description_en"].every(key => value[key] == null || (typeof value[key] === "string" && value[key].length <= 4000))
  && optionalId(value.icon_id) && optionalId(value.video_id));
const metadataValid = value => optionalId(value.video_id) && optionalId(value.exercise_id)
  && (value.exercise_version == null || (Number.isInteger(value.exercise_version) && value.exercise_version > 0))
  && exerciseSnapshot(value.exercise);

export function compileTrainingStructure(structure) {
  if (!keys(structure, ["version", "warmup_seconds", "cooldown_seconds", "runout_seconds", "blocks"])
    || ![1, 2].includes(structure.version) || ![structure.warmup_seconds, structure.cooldown_seconds, structure.runout_seconds].every(optionalTime)
    || !Array.isArray(structure.blocks) || !structure.blocks.length || structure.blocks.length > 20) return invalid();
  const blockIds = new Set();
  const annotations = structure.version === 2 ? ["exercise_id", "exercise_version", "exercise", "video_id"] : [];
  let phaseCount = [structure.warmup_seconds, structure.cooldown_seconds, structure.runout_seconds].filter(v => v != null).length;
  let totalSets = 0, repetitions = 0;
  for (const block of structure.blocks) {
    if (!keys(block, ["id", "sets", "set_rest_seconds", "intervals", ...annotations]) || !metadataValid(block) || !id(block.id) || blockIds.has(block.id)
      || !count(block.sets) || !numeric(block.set_rest_seconds, 0, 7200)
      || !Array.isArray(block.intervals) || !block.intervals.length || block.intervals.length > 100) return invalid();
    blockIds.add(block.id);
    const rowIds = new Set();
    let workCount = 0, restCount = 0;
    for (const row of block.intervals) {
      if (!keys(row, ["id", "repetitions", "work", "rest_seconds", ...annotations]) || !metadataValid(row) || !id(row.id) || rowIds.has(row.id)
        || !count(row.repetitions) || !numeric(row.rest_seconds, 0, 7200)) return invalid();
      rowIds.add(row.id);
      if (row.work?.kind === "time") {
        if (!keys(row.work, ["kind", "seconds"]) || !numeric(row.work.seconds, 1, 7200)) return invalid();
      } else if (row.work?.kind === "distance") {
        if (!keys(row.work, ["kind", "distance_m"]) || !numeric(row.work.distance_m, 1, 1000)) return invalid();
      } else if (structure.version === 2 && row.work?.kind === "repetitions") {
        if (!keys(row.work, ["kind", "count"]) || !numeric(row.work.count, 1, 1000) || !Number.isInteger(row.work.count)) return invalid();
      } else return invalid();
      workCount += row.repetitions;
      if (row.rest_seconds > 0) restCount += row.repetitions;
    }
    if (block.intervals[block.intervals.length - 1].rest_seconds > 0) restCount -= 1;
    phaseCount += block.sets * (workCount + restCount) + (block.set_rest_seconds > 0 ? block.sets - 1 : 0);
    if (phaseCount > 5000) return invalid();
    totalSets += block.sets; repetitions += workCount * block.sets;
  }
  const sections = [];
  const add = (phaseId, kind, durationMs, metadata = {}) => sections.push(Object.freeze({ id: phaseId, kind, durationMs, structured: true, ...metadata }));
  if (structure.warmup_seconds != null) add("warmup", "warmup", ms(structure.warmup_seconds));
  let globalSet = 0, globalWork = 0;
  for (const block of structure.blocks) {
    const intervalsPerSet = block.intervals.reduce((sum, row) => sum + row.repetitions, 0);
    for (let set = 1; set <= block.sets; set += 1) {
      globalSet += 1;
      let interval = 0;
      for (const row of block.intervals) for (let rep = 1; rep <= row.repetitions; rep += 1) {
        interval += 1; globalWork += 1;
        const prefix = `${block.id}:set-${set}:${row.id}:rep-${rep}`;
        const metadata = { blockId: block.id, setNumber: globalSet, sets: totalSets, blockSet: set, blockSets: block.sets,
          intervalNumber: interval, intervalsPerSet, repetition: globalWork, repetitions };
        const exercise = row.exercise || block.exercise;
        add(`${prefix}:work`, row.work.kind === "distance" ? "sprint" : row.work.kind === "repetitions" ? "repetitions" : "work", row.work.kind === "time" ? ms(row.work.seconds) : null,
          { ...metadata, ...(row.work.kind === "distance" ? { distanceM: row.work.distance_m } : {}),
            ...(row.work.kind === "repetitions" ? { workRepetitions: row.work.count } : {}),
            ...(exercise ? { exercise: Object.freeze({ ...exercise }) } : {}) });
        if (interval < intervalsPerSet && row.rest_seconds > 0) add(`${prefix}:rest`, "rest", ms(row.rest_seconds), metadata);
      }
      if (set < block.sets && block.set_rest_seconds > 0) add(`${block.id}:set-${set}:rest`, "set_rest", ms(block.set_rest_seconds),
        { blockId: block.id, setNumber: globalSet, sets: totalSets, blockSet: set, blockSets: block.sets, intervalNumber: intervalsPerSet, intervalsPerSet });
    }
  }
  if (structure.cooldown_seconds != null) add("cooldown", "cooldown", ms(structure.cooldown_seconds));
  if (structure.runout_seconds != null) add("runout", "runout", ms(structure.runout_seconds));
  const knownDurationMs = sections.reduce((sum, phase) => sum + (phase.durationMs ?? 0), 0);
  if (knownDurationMs > 86400000) return invalid();
  return { ok: true, reason: null, plan: Object.freeze({ version: structure.version === 2 ? 3 : 2, variant: "structured", totalSets, repetitions,
    sections: Object.freeze(sections), knownDurationMs, totalDurationMs: sections.some(phase => phase.durationMs === null) ? null : knownDurationMs }) };
}

export function structureDuration(structure) {
  const result = compileTrainingStructure(structure);
  return result.ok && result.plan.totalDurationMs !== null ? result.plan.totalDurationMs / 60000 : null;
}

export function trainingValidation(editor) {
  if (!editor.training_template && !editor.training_structure) return null;
  const v2 = editor.training_structure?.version === 2;
  if (!v2 && (editor.trainingUnitType !== "individual_training" || !STRUCTURED_TRAINING_TEMPLATES.includes(editor.training_template))) return "trainingStructureInvalid";
  if (v2 && !["training", "recovery"].includes(editor.category || editor.kind || "training")) return "trainingStructureInvalid";
  const result = compileTrainingStructure(editor.training_structure);
  const duration = Number(editor.duration);
  if (!result.ok || !Number.isFinite(duration) || duration <= 0 || duration > 1440) return "trainingStructureInvalid";
  if (result.plan.totalDurationMs === null ? duration * 60000 <= result.plan.knownDurationMs
    : Math.abs(duration * 60000 - result.plan.totalDurationMs) > 0.06) return "trainingStructureInvalid";
  return null;
}

export function trainingPreset(template, t = key => key) {
  if (!STRUCTURED_TRAINING_TEMPLATES.includes(template)) throw new TypeError("Unknown structured training template");
  const tennis = template === "tennis_interval";
  const structure = { version: 1, warmup_seconds: null, cooldown_seconds: null, runout_seconds: tennis ? 600 : null,
    blocks: [{ id: tennis ? "tennis" : "sidestep", sets: tennis ? 5 : 3, set_rest_seconds: tennis ? 120 : 180,
      intervals: tennis ? [[6, 20], [15, 25], [30, 45], [45, 60]].map(([seconds, rest], index) =>
        ({ id: `interval-${index + 1}`, repetitions: 1, work: { kind: "time", seconds }, rest_seconds: rest }))
        : [{ id: "sidestep", repetitions: 7, work: { kind: "time", seconds: 20 }, rest_seconds: 40 }] }] };
  return { trainingUnitType: "individual_training", training_template: template, training_structure: structure,
    duration: structureDuration(structure), title: t(`trainingTemplate_${template}`), description: t(`trainingDescription_${template}`) };
}
